import React, { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Button from "components/Button";
import Tooltip from "components/Tooltip";
import Portal from "components/Portal";
import CheckBox from "components/CheckBox";
import { useClickOutside } from "hooks/useClickOutside";
import { IconDownload, IconChevronDown, IconSketch, IconImage } from "icons";
import { DOWNLOAD_SETTING, DOWNLOAD_AI_LABEL_SETTING, useLocalStorage } from "hooks/useLocalStorage";
import { FileTypeOption, FILE_TYPE_OPTIONS, isRasterFormat } from "components/DownloadButton/constants";
import Dropdown from "components/DownloadButton/components/Dropdown";
import DownloadProgressPopup from "components/DownloadButton/components/DownloadProgressPopup";
import { useDownloadLogic } from "components/DownloadButton/hooks/useDownloadLogic";
import { useMenuPosition } from "components/DownloadButton/hooks/useMenuPosition";
import Lottie from "lottie-react";
import { useLoadingSpinner } from "hooks/useLoadingSpinner";
import designService from "api/design/design.service";
import utilsService from "api/utils/utils.service";
import { useSocket } from "hooks/useSocket";
import { useSnackbar } from "hooks/useSnackbar";
import { downloadFile, getFilenameFromUrl } from "components/DownloadButton/utils";
import { AnalyticsEvent } from "utils";
import { useAppSelector } from "store";

enum DownloadType {
  TechnicalDrawing = "technical_drawing",
  ArtisticSketch = "artistic_sketch",
}

enum SketchModeType {
  FlatSketch = "flat_sketch",
  FoldedSketch = "folded_sketch",
}

const SKETCH_MODE_KEYS: Array<{ id: SketchModeType; labelKey: string }> = [
  { id: SketchModeType.FlatSketch, labelKey: "common:technical_drawing_menu.sketch_modes.flat" },
  { id: SketchModeType.FoldedSketch, labelKey: "common:technical_drawing_menu.sketch_modes.folded" },
];

enum BackgroundModeType {
  Normal = "normal",
  Transparent = "transparent",
}

const BACKGROUND_MODE_KEYS: Array<{ id: BackgroundModeType; labelKey: string }> = [
  { id: BackgroundModeType.Normal, labelKey: "common:technical_drawing_menu.background_modes.normal" },
  { id: BackgroundModeType.Transparent, labelKey: "common:technical_drawing_menu.background_modes.transparent" },
];

interface TechnicalDrawingDownloadMenuProps {
  isDisplayed: boolean;
  imageId?: string;
  designId?: string;
  imageURL?: string;
  isSvgAvailable?: boolean;
  svgComingSoon?: boolean;
  disabled?: boolean;
  position?: "top" | "bottom" | "left" | "right";
  isImageLandscape?: boolean;
  viewType?: string;
  techpackProjectId?: string;
  renderTrigger?: (props: { onClick: (e: React.MouseEvent) => void; disabled?: boolean; isOpen: boolean }) => React.ReactNode;
  /**
   * Menu placement strategy. "auto" (default) opens above when there's space.
   * "right" right-aligns the menu with the button's right edge, opens below, and locks Y so the menu
   * doesn't jump when content swaps (e.g. type → download step).
   */
  menuPlacement?: "auto" | "right";
}

const TechnicalDrawingDownloadMenu: React.FC<TechnicalDrawingDownloadMenuProps> = ({
  isDisplayed,
  imageId,
  designId,
  imageURL,
  isSvgAvailable = true,
  svgComingSoon = false,
  disabled,
  position,
  isImageLandscape,
  viewType = "front",
  techpackProjectId = "",
  renderTrigger,
  menuPlacement = "auto",
}) => {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const downloadButtonRef = useRef<HTMLDivElement>(null);
  const loadingSpinner = useLoadingSpinner();
  const { user } = useAppSelector((state) => state.common);

  const sketchModeOptions = useMemo(() => SKETCH_MODE_KEYS.map((opt) => ({ ...opt, title: t(opt.labelKey) })), [t]);
  const backgroundModeOptions = useMemo(() => BACKGROUND_MODE_KEYS.map((opt) => ({ ...opt, title: t(opt.labelKey) })), [t]);

  const isArtisticSketchVisible = user.permissions?.artistic_sketch?.is_visible ?? false;
  const isArtisticSketchEnabled = user.permissions?.artistic_sketch?.is_enabled ?? false;

  const [isGeneratingArtisticSketch, setIsGeneratingArtisticSketch] = useState(false);
  const [artisticSketchResult, setArtisticSketchResult] = useState<any>(null);

  // Socket listener for artistic sketch
  const { lastMessage } = useSocket();
  const { showSuccess, showError } = useSnackbar();
  const lastProcessedMessageRef = useRef<string | null>(null);
  // REF-6880 (D3d): the `design_id` of the sketch job THIS menu started, from the start response (the frames carry
  // the same id, REF-6868). Only this menu reacts to that job's terminal frame, once; other menus, other tabs, a
  // menu mounted later and a replay hold no matching id. If this menu unmounts first, nobody auto-downloads the
  // result — intentional (user decision 2026-10-13): the notification / manual download is the recovery path.
  const ownedDesignIdRef = useRef<string | null>(null);
  // Captured at click time so the format conversion uses the user's selection
  // even if state happens to change before the WS frame arrives.
  const pendingFileTypeRef = useRef<string>("png");
  // REF-5701: same capture for the "Include AI Generated" toggle, for the artistic-sketch WS flow.
  const pendingAiLabelRef = useRef<boolean>(false);

  // REF-5701: "Include AI Generated" toggle — persisted (default off), shared key with the main menu.
  const [aiLabel, setAiLabel] = useState<boolean>(() => useLocalStorage().getItem(DOWNLOAD_AI_LABEL_SETTING) === true);

  const handleAiLabelChange = (checked: boolean) => {
    setAiLabel(checked);
    useLocalStorage().setItem(DOWNLOAD_AI_LABEL_SETTING, checked);
  };

  useEffect(() => {
    if (!lastMessage || lastMessage.design_type !== "techpack_artistic_sketch") return;

    const messageHash = lastMessage.messageHash || JSON.stringify(lastMessage);
    if (messageHash === lastProcessedMessageRef.current) return;
    lastProcessedMessageRef.current = messageHash;

    const isFailure = lastMessage.status === 99 || lastMessage.type === "failed";
    const isSuccess = lastMessage.type === "success" && lastMessage.status === 1;
    if (!isFailure && !isSuccess) return;
    if (!ownedDesignIdRef.current || lastMessage.design_id !== ownedDesignIdRef.current) return;
    ownedDesignIdRef.current = null;

    if (isFailure) {
      const errorMessage = lastMessage.message || t("common:technical_drawing_menu.snackbars.artistic_sketch_failed");
      showError(errorMessage);
      setIsGeneratingArtisticSketch(false);

      return;
    }

    if (isSuccess) {
      const designDetail = lastMessage.design_detail;
      const responseFile = designDetail?.images?.[0]?.path || designDetail?.images?.[0]?.edit_path;
      const selectedFileType = pendingFileTypeRef.current;

      const finalize = () => {
        setArtisticSketchResult({
          response_file: responseFile,
          design_id: lastMessage.design_id,
          images: designDetail?.images || [],
        });
        setIsGeneratingArtisticSketch(false);
      };

      if (!responseFile) {
        finalize();

        return;
      }

      const selectedAiLabel = pendingAiLabelRef.current;

      (async () => {
        try {
          let downloadUrl: string;
          // REF-5701: route through process-download when the AI label is on, or for WebP (the
          // downloadTechPack endpoint has no webp branch and would silently emit jpg). Otherwise
          // keep the existing tech-pack download path.
          if (selectedAiLabel || selectedFileType === "webp") {
            const res: any = await utilsService.processDownload({ image_url: responseFile, file_type: selectedFileType, ai_label: selectedAiLabel });
            if (res?._handled) return;
            downloadUrl = res?.responseData?.download_url ?? res?.download_url ?? responseFile;
          } else {
            const res: any = await utilsService.downloadTechPack({
              img_url: responseFile,
              file_type: selectedFileType,
            });
            if (res?._handled) return;
            downloadUrl = res?.download_url || responseFile;
          }

          const filename = getFilenameFromUrl(downloadUrl, selectedFileType);
          await downloadFile(downloadUrl, filename);
          // REF-6306: beep now centralized in handleGenerationNotification (techpack_artistic_sketch persist=True).
          showSuccess(t("common:technical_drawing_menu.snackbars.artistic_sketch_ready"));
        } catch {
          showError(t("common:technical_drawing_menu.snackbars.artistic_sketch_failed"));
        } finally {
          finalize();
        }
      })();
    }
  }, [lastMessage, showSuccess, showError, t]);

  const [showMenu, setShowMenu] = useState(false);
  const [menuStep, setMenuStep] = useState<"type" | "download">("type");
  const [downloadType, setDownloadType] = useState<DownloadType | null>(null);
  const [selectedSketchMode, setSelectedSketchMode] = useState<SketchModeType>(SketchModeType.FlatSketch);
  const [selectedBackgroundMode, setSelectedBackgroundMode] = useState<BackgroundModeType>(BackgroundModeType.Normal);
  const [isSaved, setIsSaved] = useState(useLocalStorage().getItem(DOWNLOAD_SETTING) !== null);

  const [fileType, setFileType] = useState<FileTypeOption>(() => {
    const savedFileType = useLocalStorage().getItem(DOWNLOAD_SETTING);
    const availableOptions = isSvgAvailable ? FILE_TYPE_OPTIONS : FILE_TYPE_OPTIONS.filter((option) => option.value !== "svg");

    if (savedFileType === "svg" && svgComingSoon) {
      return availableOptions.filter((option) => option.value !== "svg")[0];
    }

    return availableOptions.find((option) => option.value === savedFileType) || availableOptions[0];
  });

  const { isDownloading, showProgressPopup, download } = useDownloadLogic({
    fileType,
    designId,
    imageId,
    imageURL,
    showDownloadMenu: true,
    isTechpackDownload: downloadType === DownloadType.TechnicalDrawing,
    analyticsSurface: "technical_drawing_menu",
    aiLabel,
  });

  const menuStyle = useMenuPosition(showMenu, downloadButtonRef, menuRef, { placement: menuPlacement });

  useClickOutside(menuRef, () => {
    if (isGeneratingArtisticSketch) return; // Don't close while generating
    setShowMenu(false);
    setMenuStep("type");
    setDownloadType(null);
  });

  React.useEffect(() => {
    if (!isDisplayed) {
      setShowMenu(false);
      setMenuStep("type");
      setDownloadType(null);
    }
  }, [isDisplayed]);

  async function handleDownload() {
    if (downloadType === DownloadType.ArtisticSketch) {
      if (!imageURL || !viewType) return;

      pendingFileTypeRef.current = fileType.value;
      pendingAiLabelRef.current = aiLabel;
      setIsGeneratingArtisticSketch(true);

      AnalyticsEvent("TechpackArtisticSketch_Create", "", "click", {
        sketch_mode: selectedSketchMode,
        view_type: viewType,
        background_mode: selectedBackgroundMode,
      });

      try {
        const sketchMode = selectedSketchMode === SketchModeType.FlatSketch ? "flat_sketch" : "folded_sketch";
        const res = await designService.startTechpackArtisticSketch({
          image_url: imageURL,
          sketch_mode: sketchMode,
          view_type: viewType,
          techpack_project_id: techpackProjectId,
          background_mode: selectedBackgroundMode,
        });
        // REF-6880: own this job's result. A new start replaces the previous id (the UI blocks a second start
        // while one is generating: the button becomes a spinner and the menu cannot be closed).
        const startedId = res?.responseData?.design_id;
        ownedDesignIdRef.current = typeof startedId === "string" && startedId !== "" ? startedId : null;
        // Don't close menu here - wait for socket response
      } catch {
        setIsGeneratingArtisticSketch(false);
        setShowMenu(false);
        setMenuStep("type");
        setDownloadType(null);
      }

      return;
    }
    await download();
    setShowMenu(false);
    setMenuStep("type");
    setDownloadType(null);
  }

  // Close menu when artistic sketch result comes back (download handled by listener)
  React.useEffect(() => {
    if (artisticSketchResult && !isGeneratingArtisticSketch) {
      setShowMenu(false);
      setMenuStep("type");
      setDownloadType(null);
      setArtisticSketchResult(null);
    }
  }, [artisticSketchResult, isGeneratingArtisticSketch]);

  const handleTypeSelect = (type: DownloadType) => {
    if (type === DownloadType.ArtisticSketch && fileType.value === "svg") {
      const fallback = FILE_TYPE_OPTIONS.find((opt) => opt.value !== "svg");
      if (fallback) setFileType(fallback);
    }
    setDownloadType(type);
    setMenuStep("download");
  };

  const _fileOptions = useMemo(() => {
    const removeSvg = !isSvgAvailable || downloadType === DownloadType.ArtisticSketch;

    return removeSvg ? FILE_TYPE_OPTIONS.filter((option) => option.value !== "svg") : FILE_TYPE_OPTIONS;
  }, [isSvgAvailable, downloadType]);

  const renderFileTypeItem = (item: FileTypeOption) => {
    const Icon = item.icon;
    const isActive = item.value === fileType.value;
    const isSvgOption = item.value === "svg";
    const showSoonBadge = isSvgOption && svgComingSoon;
    const isDisabled = showSoonBadge;

    return (
      <li
        key={item.value}
        role="menuitem"
        tabIndex={isDisabled ? -1 : 0}
        className={clsx(
          "flex item-center p-[7px] border border-black-300 rounded",
          isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          isActive ? "bg-purpleGreen bg-opacity-10" : "bg-black-100"
        )}
        onClick={() => {
          if (isDisabled) return;
          setFileType(item);
          if (isSaved) useLocalStorage().setItem(DOWNLOAD_SETTING, item.value);
        }}
      >
        <div className="flex items-center w-full gap-2.5">
          <div className={clsx("rounded-full h-2.5 w-2.5", isActive ? "bg-purpleGreen" : "bg-black-300")} />
          <Icon className="text-white w-4 h-4" />
          <span className="text-white text-bodySm">{item.text}</span>
          {showSoonBadge && <span className="px-1.5 py-0.5 text-[10px] font-workSans text-black bg-purpleGreen rounded uppercase">{t("common:download_menu.soon_badge")}</span>}
          <span className="text-grey text-[11px] font-normal font-workSans leading-[15.4px] tracking-[-0.165px]">{item.description}</span>
        </div>
      </li>
    );
  };

  const renderSketchModeItem = (item: { id: SketchModeType; title: string }) => {
    const isActive = item.id === selectedSketchMode;

    return (
      <li
        key={item.id}
        role="menuitem"
        tabIndex={0}
        className={clsx("flex items-center justify-start p-[7px] border border-black-300 rounded cursor-pointer flex-1", isActive ? "bg-purpleGreen bg-opacity-10 border-purpleGreen" : "bg-black-100")}
        onClick={() => setSelectedSketchMode(item.id)}
      >
        <span className={clsx("text-bodySm text-left", isActive ? "text-purpleGreen" : "text-white")}>{item.title}</span>
      </li>
    );
  };

  const renderBackgroundModeItem = (item: { id: BackgroundModeType; title: string }) => {
    const isActive = item.id === selectedBackgroundMode;

    return (
      <li
        key={item.id}
        role="menuitem"
        tabIndex={0}
        className={clsx("flex items-center justify-start p-[7px] border border-black-300 rounded cursor-pointer flex-1", isActive ? "bg-purpleGreen bg-opacity-10 border-purpleGreen" : "bg-black-100")}
        onClick={() => setSelectedBackgroundMode(item.id)}
      >
        <span className={clsx("text-bodySm text-left", isActive ? "text-purpleGreen" : "text-white")}>{item.title}</span>
      </li>
    );
  };

  // Step 1: Type selection
  const renderTypeSelection = () => (
    <div className="flex flex-col rounded-[5px] bg-black border border-black-300">
      <div className="flex gap-[5px] text-white p-2 bg-black-100 rounded-t-[4px]">
        <IconDownload className="w-[14px] h-[14px]" />
        <span className="text-headline uppercase">{t("common:technical_drawing_menu.heading_default")}</span>
      </div>

      <div className="flex flex-col p-2.5 gap-2">
        <button
          className="flex items-center gap-2.5 p-2.5 border border-black-300 rounded hover:border-purpleGreen hover:text-purpleGreen text-white transition-colors"
          onClick={() => handleTypeSelect(DownloadType.TechnicalDrawing)}
        >
          <IconImage className="w-[18px] h-[18px]" />
          <span className="text-bodySm">{t("common:technical_drawing_menu.type_technical_drawing")}</span>
        </button>

        {isArtisticSketchVisible && (
          <button
            className={clsx(
              "flex items-center gap-2.5 p-2.5 border border-black-300 rounded transition-colors",
              isArtisticSketchEnabled ? "hover:border-purpleGreen hover:text-purpleGreen text-white" : "text-grey cursor-not-allowed opacity-50"
            )}
            onClick={() => isArtisticSketchEnabled && handleTypeSelect(DownloadType.ArtisticSketch)}
            disabled={!isArtisticSketchEnabled}
          >
            <IconSketch className="w-[18px] h-[18px]" />
            <span className="text-bodySm">{t("common:technical_drawing_menu.type_artistic_sketch")}</span>
          </button>
        )}
      </div>
    </div>
  );

  // Step 2: Download menu (with Sketch Mode if Artistic Sketch was selected)
  const renderDownloadMenu = () => (
    <div className="flex flex-col rounded-[5px] bg-black border border-black-300">
      <div className="flex gap-[5px] text-white p-2 bg-black-100 rounded-t-[4px]">
        <IconDownload className="w-[14px] h-[14px]" />
        <span className="text-headline uppercase">
          {downloadType === DownloadType.ArtisticSketch ? t("common:technical_drawing_menu.heading_artistic") : t("common:technical_drawing_menu.heading_default")}
        </span>
      </div>

      <div className="flex flex-col p-2.5 gap-2.5">
        {/* Sketch Mode - only shown when Artistic Sketch is selected */}
        {downloadType === DownloadType.ArtisticSketch && (
          <>
            <span className="text-h7 text-white">{t("common:technical_drawing_menu.sketch_mode_heading")}</span>
            <Dropdown options={sketchModeOptions} isToggle={true} renderItem={renderSketchModeItem} hideBorder={true} className="w-full gap-1 p-[7px]">
              <div className="cursor-pointer flex border border-black-300 rounded w-full">
                <div className="flex items-center gap-2.5 w-full p-[7px]">
                  <span className="text-bodySm text-white">{sketchModeOptions.find((o) => o.id === selectedSketchMode)?.title}</span>
                </div>
                <div className="flex-center border-l border-black-300 p-[7px]">
                  <IconChevronDown className="flex-shrink-0 w-[18px] h-[18px] text-white" />
                </div>
              </div>
            </Dropdown>
            <div className="flex w-full h-[1px] bg-black-300" />

            {/* Background Mode */}
            <span className="text-h7 text-white">{t("common:technical_drawing_menu.background_mode_heading")}</span>
            <Dropdown options={backgroundModeOptions} isToggle={true} renderItem={renderBackgroundModeItem} hideBorder={true} className="w-full gap-1 p-[7px]">
              <div className="cursor-pointer flex border border-black-300 rounded w-full">
                <div className="flex items-center gap-2.5 w-full p-[7px]">
                  <span className="text-bodySm text-white">{backgroundModeOptions.find((o) => o.id === selectedBackgroundMode)?.title}</span>
                </div>
                <div className="flex-center border-l border-black-300 p-[7px]">
                  <IconChevronDown className="flex-shrink-0 w-[18px] h-[18px] text-white" />
                </div>
              </div>
            </Dropdown>
            <div className="flex w-full h-[1px] bg-black-300" />
          </>
        )}

        {/* File Format Dropdown */}
        <span className="text-h7 text-white">{t("common:download_menu.file_format")}</span>
        <Dropdown options={_fileOptions} isToggle={true} renderItem={renderFileTypeItem} hideBorder={true} className="w-full gap-1 p-[7px] ">
          <div className="cursor-pointer flex border border-black-300 rounded w-full">
            <div className="flex items-center gap-2.5 w-full p-[7px]">
              <fileType.icon className="text-white w-4 h-4" />
              <span className="text-bodySm text-white">{fileType.text}</span>
            </div>
            <div className="flex-center border-l border-black-300 p-[7px]">
              <IconChevronDown className="flex-shrink-0 w-[18px] h-[18px] text-white" />
            </div>
          </div>
        </Dropdown>

        {isRasterFormat(fileType.value) && (
          <>
            <div className="flex w-full h-[1px] bg-black-300" />
            <div className="flex flex-col gap-1">
              <CheckBox
                checked={aiLabel}
                checkedColor="bg-purpleGreen"
                boxSize="lg"
                containerClassName="cursor-pointer flex items-center gap-2.5 w-fit"
                onChange={(_, isChecked: boolean) => handleAiLabelChange(isChecked)}
              >
                <span className="text-bodySm text-white">{t("common:download_menu.ai_label_toggle")}</span>
              </CheckBox>
              <span className="text-grey text-[11px] font-normal font-workSans leading-[15.4px] pl-[23px]">{t("common:download_menu.ai_label_hint")}</span>
            </div>
          </>
        )}

        {fileType.value !== "svg" && (
          <>
            <div className="flex w-full h-[1px] bg-black-300" />
            <CheckBox
              checked={isSaved}
              checkedColor="bg-purpleGreen"
              boxSize="lg"
              containerClassName="cursor-pointer flex items-center gap-2.5 w-fit"
              onChange={(_, isChecked: boolean) => {
                setIsSaved(isChecked);
                if (isChecked) useLocalStorage().setItem(DOWNLOAD_SETTING, fileType.value);
                else useLocalStorage().removeItem(DOWNLOAD_SETTING);
              }}
            >
              <span className="text-bodySm text-white">{t("common:download_menu.save_settings")}</span>
            </CheckBox>
          </>
        )}
      </div>

      <div className="flex gap-2 border-t border-black-300 p-2.5 bg-black-100 rounded-b-[4px]">
        <Button
          className="btn-secondary-small w-full"
          onClick={() => {
            setMenuStep("type");
            setDownloadType(null);
          }}
        >
          {t("common:technical_drawing_menu.back")}
        </Button>
        {isDownloading || isGeneratingArtisticSketch ? (
          <div className="flex-center w-full">
            <Lottie animationData={loadingSpinner} loop={true} className="w-8 h-8" />
          </div>
        ) : (
          <Button className="btn-primary btn-sm w-full !bg-purpleGreen" onClick={handleDownload}>
            {t("common:technical_drawing_menu.button")}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className={clsx("relative", disabled ? "pointer-events-none" : "")} ref={downloadButtonRef}>
      {renderTrigger ? (
        renderTrigger({
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            if (disabled) return;
            setShowMenu(true);
            setMenuStep("type");
          },
          disabled,
          isOpen: showMenu,
        })
      ) : (
        <Tooltip content={t("common:technical_drawing_menu.tooltip")} position={position ? position : isImageLandscape ? "top" : "left"}>
          <Button
            disabled={disabled}
            aria-label={isDownloading ? t("common:technical_drawing_menu.downloading_aria") : t("common:technical_drawing_menu.download_aria")}
            aria-expanded={showMenu}
            aria-haspopup="menu"
            className={clsx(
              "!bg-trueWhite p-2 hover:bg-green w-[26px] h-[26px] rounded-[3px] border border-black-200 dark:border-none disabled:!bg-black-300 disabled:!text-black-100 disabled:border-none",
              showMenu ? "!bg-green pointer-events-none" : "!bg-trueWhite"
            )}
            onClick={(e) => {
              e.stopPropagation();
              if (disabled) return;
              setShowMenu(true);
              setMenuStep("type");
            }}
          >
            <IconDownload className="w-4 h-4 flex-shrink-0" />
          </Button>
        </Tooltip>
      )}

      {showMenu && (
        <Portal>
          <div>
            <div
              ref={menuRef}
              className={clsx("fixed z-[10000] w-[270px]", !menuStyle.ready && "invisible")}
              style={{ top: `${menuStyle.top}px`, left: `${menuStyle.left}px` }}
              data-download-menu
              role="menu"
              aria-label={t("common:technical_drawing_menu.menu_aria")}
              onClick={(e) => e.stopPropagation()}
            >
              {menuStep === "type" && renderTypeSelection()}
              {menuStep === "download" && renderDownloadMenu()}
            </div>
          </div>
        </Portal>
      )}

      {showProgressPopup && fileType.value === "svg" && <DownloadProgressPopup imageURL={imageURL} fileType={fileType.value} />}
    </div>
  );
};

export default TechnicalDrawingDownloadMenu;
